/**
 * <main-cart-items> — cart page line-item updates via Section Rendering API.
 *
 * Loaded ONLY from `sections/main-cart_items.liquid`.
 */

import { Delegate } from '@theme/delegate';
import { addFavorite } from '@theme/favorites-storage';

class MainCartItems extends HTMLElement {
  #delegateElement = null;
  #renderToken = 0;
  #isUpdatingQuantity = false;

  connectedCallback() {
    this.#bindDelegatedEvents();
    document.addEventListener('cart:request-refresh', this.#onRequestRefresh);
  }

  disconnectedCallback() {
    this.#delegateElement?.off?.();
    this.#delegateElement = null;
    document.removeEventListener('cart:request-refresh', this.#onRequestRefresh);
  }

  // <cart-sold-out> traži re-render posle uklanjanja / save-for-later sold-out
  // kartice, da se total / empty-state vrate u konzistentno stanje.
  #onRequestRefresh = () => {
    void this.#renderCart();
  };

  #bindDelegatedEvents() {
    this.#delegateElement?.off?.();
    this.#delegateElement = new Delegate(this);

    this.#delegateElement.on(
      'click',
      '[data-action="update-item-quantity"], [data-action="remove-item"]',
      this.#updateItemQuantity
    );

    this.#delegateElement.on('click', '[data-action="save-for-later"]', this.#onSaveForLater);
    this.#delegateElement.on('click', '[data-action="cart-page-checkout"]', this.#onCheckout);
  }

  #getLocaleRoot() {
    return window.routes?.localeRootUrl || '';
  }

  async #fetchCartSection(sectionId) {
    const localeRoot = this.#getLocaleRoot();
    const isCartPage = window.location.pathname.includes('/cart');
    const baseUrl = isCartPage ? `${localeRoot}/cart` : `${localeRoot}/`;
    const response = await fetch(
      `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&_=${Date.now()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
      }
    );

    if (!response.ok) throw new Error('Section render failed');

    return response.text();
  }

  #getSoldOutLineIds() {
    return [...this.querySelectorAll('.cart-drawer__sold-out .cart-product-card[data-line-id]')]
      .map((card) => card.getAttribute('data-line-id'))
      .filter(Boolean);
  }

  #onCheckout = async (event) => {
    event.preventDefault();

    const checkoutBtn = event.target.closest('[data-action="cart-page-checkout"]');
    if (!checkoutBtn || checkoutBtn.disabled) return;

    checkoutBtn.disabled = true;

    const localeRoot = window.routes?.localeRootUrl || '';
    const soldOutLineIds = this.#getSoldOutLineIds();

    // Best-effort čišćenje živih sold-out linija pre checkout-a. Persisted
    // (već izbačene) kartice imaju stale key → ignoriši grešku, ne prekidaj.
    for (const id of soldOutLineIds) {
      try {
        await fetch(`${localeRoot}/cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id, quantity: 0 }),
        });
      } catch {
        /* stale key / offline — nastavi */
      }
    }

    window.location.assign(`${localeRoot}/checkout`);
  };

  #onSaveForLater = (event, target) => {
    event.preventDefault();
    event.stopPropagation();

    const button = target.closest('.cart-product-card__save');
    if (!button) return;

    let options = {};
    try {
      const raw = button.getAttribute('data-options');
      if (raw) options = JSON.parse(raw);
    } catch {}

    addFavorite({
      productId: button.getAttribute('data-product-id'),
      variantId: button.getAttribute('data-variant-id'),
      handle: button.getAttribute('data-product-handle'),
      options,
      variantSelected: true,
    });

    document.getElementById('favorites')?.open?.();
  };

  #setControlsBusy(busy) {
    this.classList.toggle('is-quantity-updating', busy);
  }

  #updateItemQuantity = async (event, target) => {
    event.preventDefault();

    // Drop overlapping clicks: rapid +/- taps fire concurrent /cart/change.js
    // requests that race on the server and corrupt the cart. Ignore new clicks
    // while a change is in flight; controls are disabled until the re-render.
    if (this.#isUpdatingQuantity) return;
    this.#isUpdatingQuantity = true;
    this.#setControlsBusy(true);

    const cartItem = target.closest('.cart-product-card');
    cartItem?.querySelector('.cart-product-card__loader')?.classList.add('is-visible');

    try {
      const response = await fetch(`${this.#getLocaleRoot()}/cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: target.getAttribute('data-line-id'),
          quantity: parseInt(target.getAttribute('data-quantity'), 10),
        }),
      });

      if (!response.ok) throw new Error('Cart update failed');

      await response.json();
      await this.#renderCart();
    } catch {
      cartItem?.querySelector('.cart-product-card__loader')?.classList.remove('is-visible');
    } finally {
      this.#isUpdatingQuantity = false;
      this.#setControlsBusy(false);
    }
  };

  #syncHeaderCartCount(wrapper) {
    const raw = wrapper?.dataset.cartItemCount;
    if (raw == null) return;

    const count = parseInt(raw, 10);
    if (Number.isNaN(count)) return;

    const cartCount = document.querySelector('.CartCount');
    if (cartCount) {
      cartCount.textContent = count > 0 ? `(${count})` : '';
      cartCount.hidden = count <= 0;
    }

    const mobileCartCount = document.querySelector('[data-header-cart-count]');
    if (mobileCartCount) {
      mobileCartCount.textContent = String(count);
      mobileCartCount.hidden = count <= 0;
    }

    window.theme = window.theme || {};
    window.theme.cartCount = count;
  }

  async #renderCart() {
    const token = ++this.#renderToken;
    const pageLoader = document.body.querySelector('.page-loader');
    if (pageLoader) pageLoader.style.width = '100%';

    try {
      const html = await this.#fetchCartSection('main-cart_items');
      if (token !== this.#renderToken) return;

      const tempElement = document.createElement('div');
      tempElement.innerHTML = html;

      const newWrapper = tempElement.querySelector('.main-cart__wrapper');
      const currentWrapper = this.querySelector('.main-cart__wrapper');

      if (newWrapper && currentWrapper) {
        // The info-blocks are static theme blocks rendered via {% content_for %}, which the
        // Section Rendering API fetch does not return — so the re-rendered wrapper drops them.
        // They carry no cart-dependent data, so carry the live node over (only for the full
        // layout; the empty state legitimately hides them).
        const summaryColumn = newWrapper.querySelector('.main-cart__summary-column');
        const liveInfoBlocks = currentWrapper.querySelector('.main-cart__info-blocks');
        if (summaryColumn && liveInfoBlocks) {
          const freshInfoBlocks = newWrapper.querySelector('.main-cart__info-blocks');
          if (freshInfoBlocks) {
            freshInfoBlocks.replaceWith(liveInfoBlocks);
          } else {
            summaryColumn.appendChild(liveInfoBlocks);
          }
        }

        currentWrapper.replaceWith(newWrapper);
      }

      // The header cart count lives outside this section (header-group), so replacing the
      // line items alone leaves it stale until a reload. Mirror the drawer and sync it from
      // the freshly rendered count.
      this.#syncHeaderCartCount(newWrapper);

      this.#bindDelegatedEvents();

      document
        .querySelectorAll('.cart-product-card__loader.is-visible')
        .forEach((loader) => loader.classList.remove('is-visible'));

      document.dispatchEvent(new CustomEvent('cart:refreshed'));
    } finally {
      if (token === this.#renderToken && pageLoader) {
        pageLoader.style.width = '0%';
      }
    }
  }
}

if (!customElements.get('main-cart-items')) {
  customElements.define('main-cart-items', MainCartItems);
}
